import { CSVLink } from "react-csv";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useEffect, useState } from "react";
import types from "../../utils/testTypes.js";
import { useUserContext } from "../../context/UserContext.jsx";
import CommentCell from "./CommentCell.jsx";

function formatMinSec(totalSec) {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  return `${s}s`;
}

function displayDuration(check, nowMs) {
  if (!check || check.status === "waiting" || check.status === "skipped") {
    return "—";
  }

  // Live tick while the phase is actively running
  if (check.status === "running") {
    const anchor =
      check.durationAnchorMs ||
      check.runningSinceMs ||
      (check.lastRun ? Date.parse(check.lastRun) : null);
    if (anchor && Number.isFinite(anchor)) {
      // If backend sent a duration snapshot with lastRun, keep continuity:
      // elapsed = snapshot + (now - lastRun)
      const snapshot = Number(check.duration);
      const lastRunMs = check.lastRun ? Date.parse(check.lastRun) : NaN;
      if (
        Number.isFinite(snapshot) &&
        Number.isFinite(lastRunMs) &&
        !check.durationAnchorMs &&
        !check.runningSinceMs
      ) {
        return formatMinSec(snapshot + (nowMs - lastRunMs) / 1000);
      }
      return formatMinSec((nowMs - anchor) / 1000);
    }
    if (check.duration != null) return formatMinSec(check.duration);
    return "0s";
  }

  if (check.durationFrozen && check.duration != null) {
    return check.durationFormatted || formatMinSec(check.duration);
  }
  if (check.durationFormatted) return check.durationFormatted;
  if (check.duration != null && typeof check.duration === "number") {
    return formatMinSec(check.duration);
  }
  return "—";
}

function resolvePhaseStatus(check) {
  if (!check) return null;
  if (check.status === "passed" || check.result === "success") return "passed";
  if (check.status === "failed" || check.result === "failed") return "failed";
  if (check.status === "running") return "running";
  if (check.status === "skipped") return "skipped";
  if (check.status === "waiting") return "waiting";
  if (check.status === "aborted") return "aborted";
  return check.status || null;
}

function StatusCell({ check }) {
  const phase = resolvePhaseStatus(check);

  if (check.input && !phase) {
    return null;
  }

  const badges = {
    passed: "bg-green-100 text-green-800 border-green-200",
    failed: "bg-red-100 text-red-800 border-red-200",
    running: "bg-sky-100 text-sky-800 border-sky-200",
    waiting: "bg-gray-100 text-gray-500 border-gray-200",
    skipped: "bg-gray-50 text-gray-400 border-gray-100",
    aborted: "bg-orange-100 text-orange-800 border-orange-200",
  };

  const labels = {
    passed: "Passed",
    failed: "Failed",
    running: "Running",
    waiting: "Pending",
    skipped: "—",
    aborted: "Aborted",
  };

  if (!phase) {
    return <td className="px-3 py-3 text-gray-400">—</td>;
  }

  const style = badges[phase] || "bg-gray-100 text-gray-600 border-gray-200";
  const label = labels[phase] || phase;

  return (
    <td className="px-3 py-3 align-middle whitespace-nowrap">
      <span
        className={`inline-block px-2.5 py-1 rounded border text-sm font-semibold capitalize ${style}`}
      >
        {label}
      </span>
    </td>
  );
}

function ResultCell({ result, normalizeResult }) {
  const value = normalizeResult(result);
  if (!value) {
    return <td className="px-3 py-3 text-center text-gray-400">—</td>;
  }

  const tone =
    result === "success"
      ? "bg-green-500"
      : result === "failed"
        ? "bg-red-500"
        : "bg-orange-500";

  return (
    <td className="px-3 py-3 align-middle text-center">
      <span
        className={`inline-block min-w-[5rem] px-2.5 py-1 rounded text-sm font-semibold uppercase text-white ${tone}`}
      >
        {value}
      </span>
    </td>
  );
}

export default function Checks() {
  const {
    scenario,
    checksData,
    csvData,
    abortData,
    checkingProgress,
    manualConfirm,
    fixMinMax,
  } = useUserContext();

  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    if (!checkingProgress) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [checkingProgress]);

  const normalizeResult = (value) => {
    if (!value) return value;
    let v = value;
    if (typeof v === "string") {
      const trimmed = v.trim();
      if (
        (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))
      ) {
        try {
          v = JSON.parse(trimmed);
        } catch {
          v = trimmed.replace(/^"|"$/g, "");
        }
      }
    }
    return v;
  };

  const checkKeys = Object.keys(checksData).filter(
    (c) => !/notification triggered received/i.test(c),
  );

  return (
    <div className="w-full min-w-0 max-w-full bg-white border border-gray-200 rounded shadow-sm">
      <div className="flex flex-wrap gap-3 justify-between items-center px-5 py-4 border-b border-gray-200">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-800 truncate capitalize">
            {scenario} Tests
          </h1>
          <div className="text-base mt-0.5">
            {checkingProgress ? (
              <p className="text-gray-500">Running...</p>
            ) : abortData ? (
              <p className="text-red-600">
                Aborted ( Reason: {abortData.reason} )
              </p>
            ) : checksData.completed?.result === "failed" ? (
              <p className="text-red-600">Failed</p>
            ) : (
              <p className="text-green-600">Completed</p>
            )}
          </div>
        </div>
        {(scenario === types.emergency || scenario === types.master) && (
          <button
            onClick={fixMinMax}
            className="py-1.5 px-3 text-base text-white bg-red-500 rounded active:bg-red-600 shrink-0"
          >
            Fix min-max
          </button>
        )}
      </div>

      <div className="w-full min-w-0 overflow-x-auto">
        <table className="w-full min-w-[960px] border-collapse text-base">
          <thead className="bg-gray-50 sticky top-0 z-[1]">
            <tr className="text-left text-sm font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <th className="px-4 py-3 w-[16%]">Checks</th>
              <th className="px-4 py-3 w-[10%]">Status</th>
              <th className="px-4 py-3 w-[14%]">Last Run</th>
              <th className="px-4 py-3 w-[8%]">Duration</th>
              <th className="px-4 py-3 w-[32%]">Comments</th>
              <th className="px-4 py-3 w-[10%]">Data Link</th>
              <th className="px-4 py-3 w-[10%] text-center">Result</th>
            </tr>
          </thead>
          <tbody>
            {checkKeys.map((c) => {
              const check = checksData[c];
              const isRunning = check.status === "running";
              return (
                <tr
                  key={c}
                  className={`align-top border-b border-gray-100 last:border-0 ${
                    isRunning ? "bg-sky-50" : "odd:bg-white even:bg-gray-50/70"
                  }`}
                >
                  <td className="px-4 py-3 text-lg font-medium capitalize text-sky-700 break-words">
                    {check.name}
                  </td>
                  {check.input &&
                  !["passed", "failed", "aborted"].includes(check.status) ? (
                    <td className="px-4 py-3 align-middle">
                      <button
                        onClick={() => {
                          manualConfirm(c);
                        }}
                        className="py-1 px-2 text-sm text-white capitalize rounded bg-sky-600 hover:bg-sky-800 disabled:bg-gray-500"
                        disabled={
                          !!check.input.clicked ||
                          (check.input.dependency &&
                            checksData[check.input.dependency]?.result !==
                              "success")
                        }
                      >
                        Select
                      </button>
                    </td>
                  ) : (
                    <StatusCell check={check} />
                  )}
                  <td className="px-4 py-3 break-words text-gray-600 whitespace-nowrap">
                    {check.lastRun
                      ? new Date(check.lastRun).toLocaleString()
                      : "N/A"}
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums text-gray-700 whitespace-nowrap">
                    {displayDuration(check, nowMs)}
                  </td>
                  <td className="px-4 py-3 align-top text-gray-700">
                    <CommentCell text={check.comments} />
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <CSVLink
                      data={csvData[c] ?? []}
                      filename={`${c}_${new Date().toISOString()}.csv`}
                      target="_blank"
                      className="text-base text-blue-600 hover:underline whitespace-nowrap"
                    >
                      Download CSV
                    </CSVLink>
                  </td>
                  <ResultCell
                    result={check.result}
                    normalizeResult={normalizeResult}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
